/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_PAGINATION_FIX
 * Date:         2025-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Extracted pagination logic that was lost during 2025-12-30 refactoring.
 * This hook applies client-side pagination and filtering to convert
 * allBroadcastItems/allInventoryItems into paginated broadcastItems/inventoryItems.
 *
 * Root Cause: During file splitting, the logic to populate display arrays
 * (broadcastItems, inventoryItems) from fetched data (allBroadcastItems,
 * allInventoryItems) was accidentally omitted.
 *
 * File Size Check:
 * - New file: ~80 lines
 * - Decision: Proceed (under 150 line limit for hooks)
 *
 * Related Files:
 * - client/src/components/search/hooks/useSearchPagination.ts (this file - NEW)
 * - client/src/components/search/hooks/useSearchState.ts (will import this)
 */

import { useEffect } from "react";
import { SearchState } from "../types";
import { applyBroadcastFilters } from "../utils/filterHelpers";

export const useSearchPagination = (
  state: SearchState,
  setState: React.Dispatch<React.SetStateAction<SearchState>>
) => {
  // [2025-01-01] Pagination fix: Apply pagination to broadcast items
  useEffect(() => {
    if (state.allBroadcastItems.length === 0) {
      setState(prev => ({
        ...prev,
        broadcastItems: [],
        broadcastTotal: 0,
        broadcastPages: 0,
      }));
      return;
    }

    // Apply filters to all broadcast items
    let filtered = applyBroadcastFilters(state.allBroadcastItems, {
      keyword: state.broadcastFilterKeyword,
      sku: state.broadcastSKU,
      type: state.broadcastType,
      manufacturer: state.broadcastManufacturer,
      category: state.broadcastCategory,
      qty: state.broadcastQty,
      condition: state.broadcastCondition,
      country: state.broadcastCountry,
      company: state.broadcastCompany,
    });

    // Calculate pagination
    const total = filtered.length;
    const pages = Math.ceil(total / state.broadcastItemsPerPage);
    const start = (state.broadcastPage - 1) * state.broadcastItemsPerPage;
    const end = start + state.broadcastItemsPerPage;
    const paginated = filtered.slice(start, end);

    setState(prev => ({
      ...prev,
      broadcastItems: paginated,
      broadcastTotal: total,
      broadcastPages: pages,
    }));
  }, [
    state.allBroadcastItems,
    state.broadcastFilterKeyword,
    state.broadcastSKU,
    state.broadcastType,
    state.broadcastManufacturer,
    state.broadcastCategory,
    state.broadcastQty,
    state.broadcastCondition,
    state.broadcastCountry,
    state.broadcastCompany,
    state.broadcastPage,
    state.broadcastItemsPerPage,
    setState,
  ]);

  // [2025-01-01] Pagination fix: Apply pagination to inventory items
  useEffect(() => {
    if (state.allInventoryItems.length === 0) {
      setState(prev => ({
        ...prev,
        inventoryItems: [],
        inventoryTotal: 0,
        inventoryPages: 0,
      }));
      return;
    }

    // Apply filters (simplified - adjust based on your filter logic)
    let filtered = state.allInventoryItems.filter(item => {
      if (state.inventoryFilterKeyword && !item.title?.toLowerCase().includes(state.inventoryFilterKeyword.toLowerCase())) {
        return false;
      }
      if (state.category && item.category !== state.category) {
        return false;
      }
      if (state.condition && item.condition !== state.condition) {
        return false;
      }
      if (state.manufacturer && item.manufacturer !== state.manufacturer) {
        return false;
      }
      if (state.country && item.country !== state.country) {
        return false;
      }
      if (state.minQty > 0 && item.quantity < state.minQty) {
        return false;
      }
      return true;
    });

    // Calculate pagination
    const total = filtered.length;
    const pages = Math.ceil(total / state.inventoryItemsPerPage);
    const start = (state.inventoryPage - 1) * state.inventoryItemsPerPage;
    const end = start + state.inventoryItemsPerPage;
    const paginated = filtered.slice(start, end);

    setState(prev => ({
      ...prev,
      inventoryItems: paginated,
      inventoryTotal: total,
      inventoryPages: pages,
    }));
  }, [
    state.allInventoryItems,
    state.inventoryFilterKeyword,
    state.category,
    state.condition,
    state.manufacturer,
    state.country,
    state.minQty,
    state.inventoryPage,
    state.inventoryItemsPerPage,
    setState,
  ]);

  // [2025-01-01] Pagination fix: Companies are already paginated by server
  useEffect(() => {
    setState(prev => ({
      ...prev,
      companyItems: state.allCompanyItems,
    }));
  }, [state.allCompanyItems, setState]);
};
